import { SSelect } from '@avalara/skylab-sdk/react';
import type { Connector } from '@avalara/types/index';
import { createRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { PrequalQuestionnaire, QuestionType } from '../../../../../types/prequal-questionnaire';

import { getConnectors } from '../../../lib/side-effects';
import { selectState } from '../../../selectors';

type Props = {
  onSelect: (answer: string) => void;
  disabled?: boolean;
  questionSelectedAnswers: Array<string>;
  questionId: string;
};

type ListOption = {
  label: string;
  value: string;
  selected?: boolean;
};

const isAsbPaidConnector = (connector: Connector) => !!connector.eligibleOnlineOffers?.includes('AsbPaid');

const ConnectorSearch = ({ onSelect, disabled, questionSelectedAnswers, questionId }: Props) => {
  const dispatch = useDispatch();
  const selectRef = createRef<HTMLSSelectElement>();

  const {
    data: {
      connectors: { data },
    },
    questionnaire,
  } = useSelector(selectState);

  const config = questionnaire.config as PrequalQuestionnaire;
  const { questions } = config;
  const currentQuestion = questions.find((question) => question.id === questionId) as QuestionType;
  const {
    inputData: {
      inputAnswers: { options },
    },
  } = currentQuestion;

  const mapConnectorToOption = ({ name }: Connector) => {
    const option: ListOption = { label: name, value: name };
    if (questionSelectedAnswers.length && questionSelectedAnswers.includes(option.value)) {
      option.selected = true;
    }
    return option;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (!data.length) dispatch(getConnectors());
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (disabled) void selectRef.current?.deselectAll();
  }, [disabled]);

  const asbPaidConnectors = data.filter(isAsbPaidConnector);
  const connectorsOptionsList = asbPaidConnectors.map(mapConnectorToOption);
  // remove connectors that are already shown on the page
  const connectorsToShow = connectorsOptionsList.filter(
    (connector) => !options.find((input) => connector.value === input.value),
  );

  return (
    <div
      onClick={(e) => {
        if (!disabled) e.stopPropagation();
      }}
      onKeyUp={(e) => {
        if (!disabled) e.stopPropagation();
      }}
    >
      <SSelect
        ref={selectRef}
        id='connector-select'
        inputId='connector-select'
        disabled={disabled}
        multiple
        optionsList={connectorsToShow}
        onS-select={(e) => onSelect(e.detail.item.value)}
        onS-deselect={(e) => onSelect(e.detail.item.value)}
      />
    </div>
  );
};

export default ConnectorSearch;
